import React, { useEffect, useState } from "react";
import { User } from "lucide-react";
import { usePaymentCalculations } from "./usePaymentCalculations";
import { PaymentSummaryContentProps } from "./types";

const Row: React.FC<{
  label: string;
  value: string;
  bold?: boolean;
  indent?: boolean;
  checkbox?: boolean;
  checked?: boolean;
  onCheck?: () => void;
}> = ({ label, value, bold, indent, checkbox, checked, onCheck }) => (
  <div
    className={`flex items-center justify-between py-1.5 ${indent ? "pl-0" : ""}`}
  >
    <div className="flex items-center gap-2">
      {checkbox && (
        <input
          type="checkbox"
          checked={checked}
          onChange={onCheck}
          className="w-4 h-4 rounded border-gray-300 accent-blue-600"
        />
      )}
      <span
        className={`text-sm ${bold ? "text-gray-900 font-medium" : "text-gray-600"}`}
      >
        {label}
      </span>
    </div>
    <span
      className={`text-sm ${bold ? "text-gray-900 font-semibold" : "text-gray-700"}`}
    >
      {value}
    </span>
  </div>
);

const PaymentSummaryContent: React.FC<PaymentSummaryContentProps> = ({
  selectedProducts,
  paymentMethod,
  customerName,
  discount,
  tipsAmount,
  receivedAmount,
  onReceivedAmountChange,
  time,
}) => {
  const { subtotal, discountValue, total, remainingAmount } =
    usePaymentCalculations({
      selectedProducts,
      discount,
      tipsAmount,
      receivedAmount,
    });

  const [includeOutstanding, setIncludeOutstanding] = useState(false);
  const [localAmount, setLocalAmount] = useState(
    receivedAmount === "0" ? "" : receivedAmount,
  );

  useEffect(() => {
    setLocalAmount(receivedAmount === "0" ? "" : receivedAmount);
  }, [receivedAmount]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "-" || e.key === "e" || e.key === "+") e.preventDefault();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("Text");
    if (parseFloat(pasted) < 0) e.preventDefault();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/-/g, "");
    if (/^\d*\.?\d*$/.test(val)) {
      if (val.startsWith("0") && val.length > 1 && !val.startsWith("0.")) {
        val = val.replace(/^0+/, "");
      }
      setLocalAmount(val);
      const numVal = parseFloat(val || "0");
      if (numVal > total) {
        onReceivedAmountChange(total.toFixed(2));
        setLocalAmount(total.toFixed(2));
      } else {
        onReceivedAmountChange(val === "" ? "0" : val);
      }
    }
  };

  const handleBlur = () => {
    if (localAmount === "") {
      setLocalAmount("0");
      onReceivedAmountChange("0");
    }
  };

  const isCashLike =
    paymentMethod?.toLowerCase() === "cash" ||
    paymentMethod?.toLowerCase() === "card";

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-3">
        <div className="w-9 h-9 shrink-0 rounded-full bg-white border border-gray-200 flex items-center justify-center">
          <User className="text-gray-500" size={16} />
        </div>
        <div>
          <div className="font-semibold text-gray-900 text-sm">
            Customer: {customerName || "N/A"}
          </div>
          <div className="text-sm text-gray-600 capitalize">
            Payment: {paymentMethod}
          </div>
          <div className="text-xs text-gray-400">
            Date: {time.year}-{time.month}-{time.day} | {time.hours}:
            {time.minutes}
          </div>
        </div>
      </div>

      {/* Order Summary */}
      <div>
        <h3 className="font-semibold text-gray-900 text-base pb-2 border-b border-gray-200">
          Order Summary
        </h3>
        <div className="pt-1">
          <Row label="Order Amount" value={`${subtotal.toFixed(2)} QAR`} />
          <Row label="Discount" value={`- ${discountValue.toFixed(2)} QAR`} />
          <Row label="Final Amount" value={`${subtotal.toFixed(2)} QAR`} />
        </div>
      </div>

      {/* Payment Status */}
      <div>
        <h3 className="font-semibold text-gray-900 text-base pb-2 border-b border-gray-200">
          Payment Status
        </h3>
        <div className="pt-1">
          <Row label="Total Payable" value={`${total.toFixed(2)} QAR`} bold />

          {isCashLike ? (
            <>
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-gray-600">Received Amount</span>
                <input
                  type="number"
                  value={localAmount}
                  onChange={handleInputChange}
                  onFocus={() => {
                    if (localAmount === "0") setLocalAmount("");
                  }}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  min={0}
                  max={total}
                  placeholder="0"
                  style={{ textAlign: "right" }}
                  className="w-28 h-9 rounded-md border border-gray-300 px-2.5 text-right text-sm text-gray-400 focus:outline-none focus:border-blue-400 focus:text-gray-900"
                />
              </div>

              <Row
                label="Remaining Amount"
                value={`${remainingAmount.toFixed(2)} QAR`}
              />
            </>
          ) : (
            <>
              <Row
                label="Received Amount"
                value={`${parseFloat(receivedAmount || "0").toFixed(2)} QAR`}
              />
              <Row
                label="Remaining Amount"
                value={`${remainingAmount.toFixed(2)} QAR`}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSummaryContent;
